import React from 'react';
import { 
  Box, 
  Button, 
  FormControl, 
  FormHelperText, 
  Grid as MuiGrid, 
  InputLabel, 
  MenuItem, 
  Select, 
  TextField, 
  Typography 
} from '@mui/material';
import TabPanel from './TabPanel';

// Interface for CSV file metadata including record counts
interface CsvFileMetadata {
  path: string;
  name: string;
  size?: number;
  lastModified?: string;
  record_count?: number | null;
}

interface TabPanelProcessProps {
  activeTab: number;
  inputFilePath: string;
  setInputFilePath: (path: string) => void;
  csvFiles: CsvFileMetadata[];
  isLoadingCsvFiles: boolean;
  selectedModel: string;
  setSelectedModel: (model: string) => void;
  batchSize: number | undefined;
  setBatchSize: (size: number | undefined) => void;
  priority: number | undefined;
  setPriority: (priority: number | undefined) => void;
  fetchCsvFiles: () => void;
}

const TabPanelProcess: React.FC<TabPanelProcessProps> = ({
  activeTab,
  inputFilePath,
  setInputFilePath,
  csvFiles,
  isLoadingCsvFiles,
  selectedModel,
  setSelectedModel,
  batchSize,
  setBatchSize,
  priority,
  setPriority,
  fetchCsvFiles
}) => {
  return (
    <TabPanel value={activeTab} index={4}>
      <MuiGrid container spacing={3}>
        <MuiGrid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="input-file-label">Input File Blob Path</InputLabel>
            <Select
              labelId="input-file-label"
              value={inputFilePath}
              label="Input File Blob Path"
              onChange={(e) => setInputFilePath(e.target.value)}
              required
              disabled={isLoadingCsvFiles}
            >
              {isLoadingCsvFiles ? (
                <MenuItem disabled>Loading CSV files...</MenuItem>
              ) : csvFiles.length > 0 ? (                        
                csvFiles.map((file, index) => {
                  // Extract just the filename for display
                  const displayName = file.name || file.path;
                  const fileName = file.name?.toLowerCase() || file.path.toLowerCase();
                  
                  // Determine file type and add appropriate label
                  let fileLabel = '';
                  let isLatest = false;
                  
                  if (fileName.includes('relevant')) {
                    fileLabel = ' (Module 2 - Filtered)';
                    // Check if this is the first relevant file (latest)
                    const relevantFiles = csvFiles.filter(f => f.name?.toLowerCase().includes('relevant'));
                    isLatest = relevantFiles.length > 0 && file.name === relevantFiles[0].name;
                  } else if (fileName.includes('upload')) {
                    fileLabel = ' (Original Upload)';
                    // Check if this is the first upload file (latest)
                    const uploadFiles = csvFiles.filter(f => f.name?.toLowerCase().includes('upload') && !f.name?.toLowerCase().includes('relevant'));
                    isLatest = uploadFiles.length > 0 && file.name === uploadFiles[0].name;
                  } else {
                    fileLabel = ' (Original Keywords)';
                  }
                  
                  // Format record count display
                  let recordCountDisplay = '';
                  if (file.record_count !== undefined) {
                    if (file.record_count === null) {
                      recordCountDisplay = ' [Count: Error]';
                    } else {
                      recordCountDisplay = ` [${file.record_count.toLocaleString()} records]`;
                    }
                  }
                  
                  return (
                    <MenuItem key={file.path} value={file.path}>
                      {displayName}{fileLabel}{isLatest ? ' (Latest)' : ''}{recordCountDisplay}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem disabled>No CSV files found</MenuItem>
              )}
            </Select>
            <FormHelperText>
              {isLoadingCsvFiles 
                ? "Loading CSV files..." 
                : "Select a CSV file: Module 2 filtered files (relevant.csv) or original keyword uploads"}
            </FormHelperText>
          </FormControl>
          <Button
            variant="outlined"
            onClick={() => {
              console.log("Manual refresh clicked");
              fetchCsvFiles();
            }}
            disabled={isLoadingCsvFiles}
            sx={{ mt: 1, ml: 1, height: 40 }}
          >
            Refresh Files
          </Button>
        </MuiGrid>
        <MuiGrid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="model-select-label">Model</InputLabel>
            <Select
              labelId="model-select-label"
              value={selectedModel}
              label="Model"
              onChange={(e) => setSelectedModel(e.target.value)}
            >
              <MenuItem value="nano">Nano Model</MenuItem>
              <MenuItem value="batch">Batch Model</MenuItem>
            </Select>
          </FormControl>
        </MuiGrid>
        <MuiGrid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Batch Size"
            value={batchSize === undefined ? '' : batchSize}
            onChange={(e) => setBatchSize(e.target.value === '' ? undefined : parseInt(e.target.value))}
            helperText="Number of items to process in each batch (optional)"
            inputProps={{ min: 1 }}
          />
        </MuiGrid>
        <MuiGrid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Priority"
            value={priority === undefined ? '' : priority}
            onChange={(e) => setPriority(e.target.value === '' ? undefined : parseInt(e.target.value))}
            helperText="Job priority level (optional, higher number = higher priority)"
            inputProps={{ min: 1, max: 10 }}
          />
        </MuiGrid>
        <MuiGrid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Once you've configured all settings, click the "Start Batch Process" button below to begin processing.
          </Typography>
        </MuiGrid>
      </MuiGrid>
    </TabPanel>
  );
};

export default TabPanelProcess;
